import { supabase } from './supabaseClient';

interface UploadImageResponse {
  success: boolean;
  data?: {
    id: string;
    filename: string;
    uploaded: string;
    requireSignedURLs: boolean;
    variants: string[];
    url: string;
  };
  message?: string;
  errors?: any[];
}

interface DeleteImageResponse {
  success: boolean;
  message?: string;
  errors?: any[];
}

export const uploadImage = async (file: File, metadata?: Record<string, string>): Promise<UploadImageResponse> => {
  try {
    console.log('Starting upload process:', file.name, file.type, file.size);
    
    const formData = new FormData();
    formData.append('image', file);
    
    if (metadata) {
      console.log('Adding metadata:', metadata);
      formData.append('metadata', JSON.stringify(metadata));
    }

    console.log('Sending request to upload image API...');
    const response = await fetch('/api/upload/image', {
      method: 'POST',
      body: formData,
    });

    console.log('Response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText);
      return {
        success: false,
        message: `Upload failed with status: ${response.status} - ${errorText || response.statusText}`
      };
    }
    
    try {
      const data = await response.json();
      console.log('Response data:', data);
      return data;
    } catch (jsonError) {
      console.error('Error parsing JSON response:', jsonError);
      return {
        success: false,
        message: `Error parsing server response: ${jsonError}`
      };
    }
  } catch (error) {
    console.error('Error uploading image:', error);
    return {
      success: false,
      message: error instanceof Error ? `Không thể tải lên ảnh: ${error.message}` : 'Không thể tải lên ảnh'
    };
  }
};

export const deleteImage = async (imageId: string): Promise<DeleteImageResponse> => {
  try {
    console.log('Deleting image with ID:', imageId);
    
    const response = await fetch(`/api/upload/image/${imageId}`, {
      method: 'DELETE',
    });

    console.log('Delete response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText);
      return {
        success: false,
        message: `Delete failed with status: ${response.status} - ${errorText || response.statusText}`
      };
    }
    
    try {
      const data = await response.json();
      console.log('Delete response data:', data);
      return data;
    } catch (jsonError) {
      console.error('Error parsing JSON response:', jsonError);
      return {
        success: false,
        message: `Error parsing server response: ${jsonError}`
      };
    }
  } catch (error) {
    console.error('Error deleting image:', error);
    return {
      success: false,
      message: error instanceof Error ? `Không thể xóa ảnh: ${error.message}` : 'Không thể xóa ảnh'
    };
  }
};

// Trích xuất ID ảnh từ Cloudflare Images URL
export const extractImageIdFromUrl = (url: string): string | null => {
  if (!url) return null;
  // URL format: https://imagedelivery.net/account_hash/image_id/variant
  const regex = /imagedelivery\.net\/[^\/]+\/([^\/]+)/;
  const match = url.match(regex);
  return match ? match[1] : null;
};

// Đổi biến thể của ảnh (public, thumbnail, etc.)
export const changeImageVariant = (url: string, variant: string = 'public'): string => {
  if (!url || !url.includes('imagedelivery.net')) {
    return url;
  }
  // Thay thế biến thể cuối cùng của URL
  return url.replace(/\/[^\/]+$/, `/${variant}`);
};

/**
 * Dịch vụ xử lý hình ảnh từ GetInstantImages API
 * Phục vụ việc lấy và xử lý URL hình ảnh trực tiếp
 */

interface ImageResult {
  id: string;
  url: string;
  description?: string;
  source: string;
  author?: {
    name: string;
    url?: string;
  };
}

// Cache lưu trữ kết quả tìm kiếm hình ảnh để tránh gọi API nhiều lần
const imageCache: Record<string, ImageResult[]> = {};

/**
 * Tìm kiếm hình ảnh và trả về mảng các kết quả
 * @param keyword Từ khóa tìm kiếm
 * @param provider Nhà cung cấp hình ảnh (unsplash, pexels, pixabay)
 * @returns Promise chứa mảng kết quả hình ảnh
 */
export async function searchImages(
  keyword: string,
  provider: 'unsplash' | 'pexels' | 'pixabay' = 'pexels'
): Promise<ImageResult[]> {
  // Tạo cache key từ keyword và provider
  const cacheKey = `${provider}:${keyword}`;
  
  // Nếu đã có trong cache, trả về luôn
  if (imageCache[cacheKey]) {
    return imageCache[cacheKey];
  }

  try {
    // Tạo URL API dựa trên provider
    let apiUrl;
    switch (provider) {
      case 'unsplash':
        apiUrl = `https://proxy.getinstantimages.com/api/unsplash?type=search&content_filter=low&page=1&term=${encodeURIComponent(keyword)}&version=7.0.1`;
        break;
      case 'pixabay':
        apiUrl = `https://proxy.getinstantimages.com/api/pixabay?type=search&term=${encodeURIComponent(keyword)}&page=1&image_type=photo&orientation=horizontal&version=7.0.1`;
        break;
      case 'pexels':
      default:
        apiUrl = `https://proxy.getinstantimages.com/api/pexels?type=search&term=${encodeURIComponent(keyword)}&page=1&order_by=curated&version=7.0.1`;
    }

    // Gọi API
    const response = await fetch(apiUrl, {
      headers: {
        'User-Agent': 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10.15; rv:136.0) Gecko/20100101 Firefox/136.0',
        'Accept': '*/*',
        'Accept-Language': 'vi-VN,vi;q=0.8,en-US;q=0.5,en;q=0.3',
        'Referer': 'https://buddhistinsights.com/',
        'Origin': 'https://buddhistinsights.com'
      }
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    
    // Xử lý kết quả theo provider
    let results: ImageResult[] = [];
    
    switch (provider) {
      case 'unsplash':
        if (data.results && Array.isArray(data.results)) {
          results = data.results.map((item: any) => ({
            id: item.id,
            url: item.urls?.regular || item.urls?.small || '',
            description: item.description || item.alt_description || '',
            source: 'Unsplash',
            author: {
              name: item.user?.name || 'Unknown',
              url: item.user?.links?.html || ''
            }
          }));
        }
        break;
        
      case 'pixabay':
        if (data.hits && Array.isArray(data.hits)) {
          results = data.hits.map((item: any) => ({
            id: String(item.id),
            url: item.webformatURL || item.largeImageURL || '',
            description: item.tags || '',
            source: 'Pixabay',
            author: {
              name: item.user || 'Unknown',
              url: item.userImageURL || ''
            }
          }));
        }
        break;
        
      case 'pexels':
      default:
        if (data.photos && Array.isArray(data.photos)) {
          results = data.photos.map((item: any) => ({
            id: String(item.id),
            url: item.src?.medium || item.src?.large || '',
            description: item.alt || '',
            source: 'Pexels',
            author: {
              name: item.photographer || 'Unknown',
              url: item.photographer_url || ''
            }
          }));
        } else if (data.results && Array.isArray(data.results)) {
          // Một số API trả về dưới dạng results thay vì photos
          results = data.results.map((item: any) => ({
            id: String(item.id),
            url: item.src?.medium || item.src?.large || item.url || '',
            description: item.alt || item.description || '',
            source: 'Pexels',
            author: {
              name: item.photographer || 'Unknown',
              url: item.photographer_url || ''
            }
          }));
        }
        break;
    }
    
    // Lọc ra các kết quả có URL hợp lệ
    const validResults = results.filter(item => item.url);
    
    // Lưu vào cache
    imageCache[cacheKey] = validResults;
    
    return validResults;
  } catch (error) {
    console.error('Error searching images:', error);
    return [];
  }
}

/**
 * Lấy một URL hình ảnh trực tiếp dựa vào từ khóa
 * @param keyword Từ khóa tìm kiếm
 * @param provider Nhà cung cấp hình ảnh
 * @param fallbackUrl URL dự phòng nếu không tìm thấy
 * @returns Promise chứa URL hình ảnh
 */
export async function getImageUrl(
  keyword: string,
  provider: 'unsplash' | 'pexels' | 'pixabay' = 'pexels',
  fallbackUrl: string = 'https://images.pexels.com/photos/268533/pexels-photo-268533.jpeg'
): Promise<string> {
  try {
    const results = await searchImages(keyword, provider);
    
    if (results.length > 0) {
      // Lấy một hình ảnh ngẫu nhiên từ kết quả
      const randomIndex = Math.floor(Math.random() * Math.min(results.length, 5));
      return results[randomIndex].url;
    }
    
    return fallbackUrl;
  } catch (error) {
    console.error('Error getting image URL:', error);
    return fallbackUrl;
  }
}

// Danh sách các URL hình ảnh đã xác nhận hoạt động tốt
export const VERIFIED_IMAGES = {
  meditation: 'https://images.pexels.com/photos/3094230/pexels-photo-3094230.jpeg',
  buddha: 'https://images.pexels.com/photos/6152103/pexels-photo-6152103.jpeg',
  temple: 'https://images.pexels.com/photos/5326901/pexels-photo-5326901.jpeg',
  zen: 'https://images.pexels.com/photos/3571551/pexels-photo-3571551.jpeg',
  nature: 'https://images.pexels.com/photos/3225517/pexels-photo-3225517.jpeg',
  lotus: 'https://images.pexels.com/photos/5537395/pexels-photo-5537395.jpeg',
  path: 'https://images.pexels.com/photos/1034887/pexels-photo-1034887.jpeg',
  dharma: 'https://images.pexels.com/photos/4388290/pexels-photo-4388290.jpeg',
  mindfulness: 'https://images.pexels.com/photos/1051838/pexels-photo-1051838.jpeg',
  wisdom: 'https://images.pexels.com/photos/2662116/pexels-photo-2662116.jpeg'
}; 